import { useState } from "react"
import { Modal, Form, Input, Button, Space, Typography, theme } from "antd"
import { LockOutlined, UserOutlined, MailOutlined } from "@ant-design/icons"
import { UserWithTenant } from "@/features/superadmin/types"
import { toast } from "sonner"

const { Text, Paragraph } = Typography;

interface ResetPasswordDialogProps {
  user: UserWithTenant | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (userId: string, newPassword: string, permanent?: boolean) => Promise<void>
}

export function ResetPasswordDialog({
  user,
  open,
  onOpenChange,
  onSubmit
}: ResetPasswordDialogProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { token } = theme.useToken();

  if (!user) {
    return null;
  }

  const handleFinish = async (values: any) => {
    const { newPassword, confirmPassword } = values;

    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    setLoading(true);
    try {
      await onSubmit(user.id, newPassword);
      toast.success(`Senha do usuário ${user.first_name || user.username} redefinida com sucesso!`);

      form.resetFields();
      onOpenChange(false);
    } catch (error) {
      console.error('Erro ao redefinir senha:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onOpenChange(false);
  };

  return (
    <Modal
      title={
        <Space>
          <LockOutlined />
          <span>Redefinir Senha de Usuário</span>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          Cancelar
        </Button>,
        <Button key="submit" type="primary" onClick={() => form.submit()} loading={loading}>
          Redefinir Senha
        </Button>
      ]}
      width={480}
      styles={{
        header: { padding: '20px 24px 0' },
        body: { padding: '20px 24px' },
        footer: { padding: '16px 24px', margin: 0 },
      }}
    >
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ backgroundColor: token.colorInfoBg, padding: 16, borderRadius: 8, border: `1px solid ${token.colorInfoBorder}` }}>
          <Space orientation="vertical" size={2}>
            <Text strong><UserOutlined /> {user.first_name} {user.last_name}</Text>
            <Text type="secondary" style={{ fontSize: '13px' }}><MailOutlined /> {user.email}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>Username: {user.username}</Text>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
        >
          <Form.Item
            name="newPassword"
            label="Nova Senha"
            rules={[
              { required: true, message: 'Por favor, insira a nova senha' },
              { min: 6, message: 'A senha deve ter pelo menos 6 caracteres' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Senha segura" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirmar Nova Senha"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Por favor, confirme a nova senha' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('As senhas não coincidem!'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Confirme a senha" />
          </Form.Item>

          <Paragraph type="secondary" style={{ fontSize: '12px', marginTop: -12 }}>
            A nova senha passa a valer imediatamente. O usuário pode alterá-la depois em Meu Perfil.
          </Paragraph>
        </Form>
      </Space>
    </Modal>
  );
}
